import { download as bulkDownload } from './bulk-downloader.js';
import { sanitizeFilenamePart, truncateFilenameBase } from './string-utils.js';

const DEFAULT_PRESIGN_URL = 'https://api.nthround.io/portal/auth/get-presigned-url';

function ensureModal() {
	let modal = document.getElementById('bulkDownloadModal');
	if (!modal) {
		modal = document.createElement('div');
		modal.id = 'bulkDownloadModal';
		modal.className = 'modal modal-sm';
		modal.innerHTML = '<div class="modal-content"><div id="bulkDownloadModalBody" class="p-2 center-align"></div></div>';
		document.body.appendChild(modal);
	}
	return modal;
}

/**
 * Creates a reusable bulk-download modal.
 *
 * @param {object} [opts]
 * @param {string} [opts.zipBaseName='Documents'] - Base name for the generated zip file(s)
 * @param {string} [opts.presignUrl] - API endpoint for presigning S3 keys
 * @returns {{ openConfirm(docs: Array): void }}
 */
export function createBulkDownloadModal({ zipBaseName = 'Documents', presignUrl = DEFAULT_PRESIGN_URL } = {}) {
	function showDlModal(state, opts) {
		const modal = ensureModal();
		const body = document.getElementById('bulkDownloadModalBody');
		if (!body) return;
		let inst = M.Modal.getInstance(modal) || M.Modal.init(modal, { dismissible: false });

		if (state === 'confirm') {
			const docs = opts.docs;
			const byYear = {};
			const byType = {};
			docs.forEach(function (d) {
				const y = new Date(Date.parse(d.date)).getFullYear().toString();
				byYear[y] = (byYear[y] || 0) + 1;
				const t = d.type || (d.tag ? d.tag.split(',')[0].trim() : '') || 'Other';
				byType[t] = (byType[t] || 0) + 1;
			});
			const yearRows = Object.keys(byYear)
				.sort(function (a, b) {
					return b - a;
				})
				.map(function (y) {
					return (
						'<div style="display:flex;justify-content:space-between;padding:2px 0"><span>' +
						y +
						'</span><span class="text-secondary ml-4">' +
						byYear[y] +
						'</span></div>'
					);
				})
				.join('');
			const typeKeys = Object.keys(byType).sort();
			const typeRows = typeKeys
				.map(function (t) {
					return (
						'<div style="display:flex;justify-content:space-between;padding:2px 0"><span>' +
						t +
						'</span><span class="text-secondary ml-4">' +
						byType[t] +
						'</span></div>'
					);
				})
				.join('');
			body.innerHTML =
				'<h3 class="text-lg mb-2 mt-2 font-bold">Download ' +
				docs.length +
				' document' +
				(docs.length !== 1 ? 's' : '') +
				'</h3>' +
				'<div style="text-align:left;max-width:280px;margin:0 auto 16px;font-size:.85rem">' +
				'<div class="text-xs font-bold text-secondary mb-1 mt-3">BY YEAR</div>' +
				yearRows +
				(typeKeys.length ? '<div class="text-xs font-bold text-secondary mb-1 mt-3">BY TYPE</div>' + typeRows : '') +
				'</div>' +
				'<a href="#!" class="btn-flat modal-close waves-effect mr-2">Cancel</a>' +
				'<a href="#!" id="dlConfirmBtn" class="btn bg-grey-darker waves-effect waves-light">Download ' +
				docs.length +
				' file' +
				(docs.length !== 1 ? 's' : '') +
				'</a>';
			M.Modal.init(modal, { dismissible: true });
			M.Modal.getInstance(modal).open();
			document.getElementById('dlConfirmBtn').addEventListener(
				'click',
				function () {
					startBulkDownload(docs);
				},
				{ once: true }
			);
		} else if (state === 'loading') {
			body.innerHTML =
				'<div class="mb-4 mt-4" style="display:flex;justify-content:center">' +
				'<svg class="spinner" viewBox="0 0 50 50" style="width:60px;height:60px">' +
				'<circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="3"></circle></svg></div>' +
				'<h3 id="dlProgressHeader" class="text-lg mb-2 mt-4 font-bold">Preparing your download</h3>' +
				'<span id="dlProgressMessage" class="text-xs text-secondary">Starting...</span>';
			inst.options.dismissible = false;
			inst.open();
		} else if (state === 'done') {
			const succeeded = opts && opts.succeeded != null ? opts.succeeded : null;
			const failedFiles = opts && opts.failed && opts.failed.length ? opts.failed : [];
			const total = succeeded != null ? succeeded + failedFiles.length : null;
			const countLine =
				total != null
					? '<p class="text-sm text-secondary mt-0 mb-2">' + succeeded + ' of ' + total + ' file' + (total !== 1 ? 's' : '') + ' downloaded</p>'
					: '';
			const failedHtml = failedFiles.length
				? '<div style="text-align:left;max-width:320px;margin:8px auto 0">' +
					'<div class="text-xs font-bold text-secondary mb-1"><span style="color:#e53935">' +
					failedFiles.length +
					'</span> FAILED TO DOWNLOAD</div>' +
					failedFiles
						.map(function (f) {
							return (
								'<div class="text-xs text-secondary" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding:1px 0">' +
								f +
								'</div>'
							);
						})
						.join('') +
					'</div>'
				: '';
			body.innerHTML =
				'<div class="mb-2"><img src="./../../resources/assets/celebrate-v4.svg" class="responsive-img px-12 mt-4"/></div>' +
				'<h3 class="text-lg mb-1 mt-2 font-bold">Download complete</h3>' +
				countLine +
				failedHtml +
				'<a href="#!" class="btn bg-grey-darker mt-4 modal-close">Done</a>';
			M.Modal.init(modal, { dismissible: true });
			M.Modal.getInstance(modal).open();
		} else {
			const msg = opts && opts.message ? opts.message : 'Some files could not be downloaded.';
			body.innerHTML =
				'<div class="mb-4 mt-4" style="font-size:52px">&#9888;&#65039;</div>' +
				'<h3 class="text-lg mb-4 mt-4 font-bold">Something went wrong</h3>' +
				'<p class="text-xs text-secondary">' +
				msg +
				'</p>' +
				'<a href="#!" class="btn bg-grey-darker mt-4 modal-close">Close</a>';
			M.Modal.init(modal, { dismissible: true });
			M.Modal.getInstance(modal).open();
		}
	}

	async function startBulkDownload(docs) {
		showDlModal('loading');
		try {
			const usedNames = new Set();
			const docsWithNames = docs.map(function (d) {
				const keyBase = d.url.split('?')[0];
				const keyFile = keyBase.slice(keyBase.lastIndexOf('/') + 1);
				const dot = keyFile.lastIndexOf('.');
				const ext = dot >= 0 ? keyFile.slice(dot).toLowerCase() : '';
				const year = d.date ? new Date(Date.parse(d.date)).getFullYear().toString() : 'Other';
				const type = d.type || (d.tag ? d.tag.split(',')[0].trim() : '') || 'Other';
				const folder = year + '/' + type + '/';
				const desc = d.description
					? ' - ' +
						d.description
							.replace(/[\\/:*?"<>|]/g, '')
							.trim()
							.slice(0, 40)
							.trim()
					: '';
				// #5839: sanitize/cap d.name same as desc above so this doesn't exceed Windows MAX_PATH
				const baseName = truncateFilenameBase(sanitizeFilenamePart(d.name), 60) + desc;
				let candidate = folder + baseName + ext;
				if (usedNames.has(candidate)) {
					let n = 2;
					while (usedNames.has(folder + baseName + ' (' + n + ')' + ext)) n++;
					candidate = folder + baseName + ' (' + n + ')' + ext;
				}
				usedNames.add(candidate);
				return { url: d.url, zipName: candidate };
			});

			const shareholder = (userData.userid || '').replace(/\+/g, 'PLUS');
			const resolved = await Promise.all(
				docsWithNames.map(async (d) => {
					let url = d.url;
					if (!url.startsWith('http')) {
						const resp = await axios.get(presignUrl, {
							params: { client, userid: authedUser, cookie, shareholder, doc_name: url },
						});
						url = resp.data;
					}
					return { name: d.zipName, url };
				})
			);
			const failed = [];
			await bulkDownload(resolved, {
				zipBaseName,
				onProgress({ phase, done, total, part, parts, fileName }) {
					const header = document.getElementById('dlProgressHeader');
					const msg = document.getElementById('dlProgressMessage');
					if (phase === 'downloading') {
						if (header) header.textContent = 'Downloading files...';
						if (msg) msg.textContent = done + ' of ' + total;
					} else if (phase === 'compressing') {
						if (header) header.textContent = parts > 1 ? 'Creating zip ' + part + ' of ' + parts + '...' : 'Creating zip...';
						if (msg) msg.textContent = '';
					} else if (phase === 'error') {
						failed.push(fileName);
					}
				},
			});
			showDlModal('done', { succeeded: resolved.length - failed.length, failed });
		} catch (err) {
			showDlModal('error', { message: err.message });
		}
	}

	return {
		openConfirm(docs) {
			showDlModal('confirm', { docs });
		},
	};
}
